import { action, computed, makeAutoObservable, observable, runInAction } from 'mobx';

import { ContentListProviders, ContentType } from '@smart-tv/typings/content';

import { convertDatesToUnix } from '../../../utils/convertDatesToUnix';
import { RootStore } from './store-root';
import { ALL_EPISODES_LIMIT } from './store-episodes';

type SeasonsParams = {
  seriesId: number;
  sid?: number;
  eventId?: number;
  universeId?: number;
  providerCollectionId?: number;
  providers?: ContentListProviders;
  contentType?: ContentType;
  subProviderId?: string;
  sheetData?: any;
};

export type Season = {
  episodeFilters: Seasons[];
  seasonNumber: number;
};

export type Seasons = {
  seasonIds: number;
  filterTitle: string;
};

class SeasonsStore {
  rootStore: RootStore;
  seasonsData: Season = { episodeFilters: [], seasonNumber: 0 };
  dialogSeasonPicker = false;
  selectedSeasonTitle = '';
  seasonFilterActive = false;
  selectedSeasonId: number | null = null;

  constructor(root: RootStore) {
    this.rootStore = root;
    makeAutoObservable(this, {
      seasonsData: observable,
      fetchSeasons: action.bound,
      setSelectedSeasonTitle: action.bound,
      resetSeasons: action.bound,
      seasons: computed,
      setSeasonFilterActive: action.bound,
      setSelectedSeasonId: action.bound
    });
  }

  setSeasonFilterActive = (value: boolean) => {
    this.seasonFilterActive = value;
  };

  setSelectedSeasonId = (id: number | null) => {
    this.selectedSeasonId = id;
  };

  resetSeasons = () => {
    this.seasonsData = [] as any;
    this.selectedSeasonId = null;
    this.selectedSeasonTitle = '';
    this.seasonFilterActive = false;
  };

  setSelectedSeasonTitle = (title: string) => {
    this.selectedSeasonTitle = title;
  };

  get seasons(): Seasons[] {
    if (!this.seasonsData.episodeFilters) {
      return [];
    }

    return this.seasonsData.episodeFilters;
  }

  fetchSeasons = async (params: SeasonsParams) => {
    const { seriesId, universeId, providerCollectionId, subProviderId, sheetData } = params;
    this.resetSeasons();
    this.setSelectedSeasonTitle('');

    const convertResult = convertDatesToUnix(sheetData);

    runInAction(() => {
      this.seasonsData = convertResult;
    });
    return runInAction(() => {
      let seasonsIds = [];
      if (convertResult.episodeFilters && convertResult.episodeFilters.length > 0) {
        seasonsIds = convertResult.episodeFilters.map((season: any) => season.seasonIds);
      }

      const episodeParams = {
        seasonIds: seasonsIds,
        contentId: seriesId ?? undefined,
        contentType: params.contentType,
        start: 0,
        limit: ALL_EPISODES_LIMIT,
        universeId: universeId ?? undefined,
        providerCollectionId: providerCollectionId ?? undefined,
        providers: ContentListProviders.Subscribed,
        subProviderId
      };

      return this.rootStore.episodesStore.fetchEpisodes(episodeParams);
    });
  };
}

export default SeasonsStore;
